import {inject} from '@angular/core';
import {catchError, concatMap, exhaustMap, finalize, map, mergeMap, of, switchMap, tap, withLatestFrom} from 'rxjs';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {AgentsHttpService} from "../services/agents-http.service";
import {AgentsActions, AgentsAPiActions, AgentsChannelActions} from "./agents.actions";
import {AgentsChanelService} from "../services/agents-chanel.service";
import {AgentModel} from "../models/agent.model";
import {CommonsFacadeService} from "../../../commons/angular/state/commons/commons-facade.service";
import {Store} from "@ngrx/store";
import {agentsFeature} from "./agents.state";

export const getAgents = createEffect(
  (actions$ = inject(Actions),
   agentsHttpService: AgentsHttpService = inject(AgentsHttpService),
  ) => {
    return actions$.pipe(
      ofType(AgentsAPiActions.getAgents),
      mergeMap(payload => {
        return agentsHttpService.getAgents(payload.params).pipe(
          map((res: {
            list: AgentModel[]
            cursor: string
          }) => AgentsAPiActions.getAgentsSuccess({agents: res.list, isSubscribeToChannel: payload.isSubscribeToChannel})),
          catchError((error: { message: string }) => of(AgentsAPiActions.getAgentsFailure({error}))),
        )
      })
    );
  },
  {functional: true}
);

export const getAgentsSuccess = createEffect(
  (actions$ = inject(Actions),
   agentsChannelService = inject(AgentsChanelService)) => {
    return actions$.pipe(
      ofType(AgentsAPiActions.getAgentsSuccess),
      map((payload) => {
        if (payload.isSubscribeToChannel) {
          const agentIds = payload.agents.map(agent => agent.id);
          agentIds.forEach(agentId => agentsChannelService.subscribeById(agentId));
        }
      })
    );
  },
  {functional: true, dispatch: false}
);

export const getInfiniteScrollAgents = createEffect(
  (actions$ = inject(Actions),
   agentsHttpService = inject(AgentsHttpService),
   agentsStore = inject(Store<AgentModel>),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(AgentsAPiActions.getInfiniteScrollAgents),
      concatMap(payload =>
        of(payload).pipe(
          withLatestFrom(agentsStore.select(agentsFeature.selectCursor()))
        )
      ),
      mergeMap(([payload, cursor]) => {
        let paramsCopy = {...payload.params}
        if (!!cursor) {
          paramsCopy.cursor = cursor;
        }
        return agentsHttpService.getAgents(paramsCopy).pipe(
          map((res: {
            list: AgentModel[]
            cursor: string
          }) => AgentsAPiActions.getInfiniteScrollAgentsSuccess({agents: res.list, cursor: res.cursor, isSubscribeToChannel: payload.isSubscribeToChannel})),
          catchError((error: {
            message: string
          }) => of(AgentsAPiActions.getInfiniteScrollAgentsFailure({error})))
        )
      })
    );
  },
  {functional: true}
);

export const getInfiniteScrollAgentsSuccess = createEffect(
  (actions$ = inject(Actions),
   agentsChannelService = inject(AgentsChanelService),
   agentsStore = inject(Store<AgentModel>)) => {
    return actions$.pipe(
      ofType(AgentsAPiActions.getInfiniteScrollAgentsSuccess),
      map((payload) => {
        if (payload.isSubscribeToChannel) {
          const agentIds = payload.agents.map(l => l.id);
          agentIds.forEach(id => agentsChannelService.subscribeById(id));
        }
      })
    );
  },
  {functional: true, dispatch: false}
);

export const getAgent = createEffect(
  (actions$ = inject(Actions),
   agentsHttpService: AgentsHttpService = inject(AgentsHttpService),
   commonsFacadeService: CommonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(AgentsAPiActions.getAgent),
      mergeMap(payload => {
        commonsFacadeService.setLoaderOn();
        return agentsHttpService.getOne(payload.id).pipe(
          map((agent: AgentModel) => AgentsAPiActions.getAgentSuccess({agent: agent, isSubscribeToChannel: payload.isSubscribeToChannel})
          ),
          catchError((error: { message: string }) => of(AgentsAPiActions.getAgentFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff();
          })
        )
      })
    );
  },
  {functional: true}
);

export const getAgentSuccess = createEffect(
  (actions$ = inject(Actions),
   agentsChannelService = inject(AgentsChanelService),
   agentsStore = inject(Store<AgentModel>)) => {
    return actions$.pipe(
      ofType(AgentsAPiActions.getAgentSuccess),
      map((payload) => {
        if (payload.isSubscribeToChannel) {
          agentsChannelService.subscribeById(payload.agent.id);
        }
      })
    );
  },
  {functional: true, dispatch: false}
);

export const updateAgent = createEffect(
  (actions$ = inject(Actions),
   agentsHttpService: AgentsHttpService = inject(AgentsHttpService),
   agentsChanelService: AgentsChanelService = inject(AgentsChanelService),
   commonsFacadeService: CommonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(AgentsAPiActions.updateAgent),
      mergeMap(payload => {
        commonsFacadeService.setLoaderOn();
        return agentsHttpService.updateOne(payload.id, payload.agent).pipe(
          map((agent: AgentModel) => AgentsAPiActions.updateAgentSuccess({agent: agent})),
          catchError((error: { message: string }) => of(AgentsAPiActions.updateAgentFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff();
          })
        )
      })
    );
  },
  {functional: true}
);

export const deleteAgent = createEffect(
  (actions$ = inject(Actions),
   agentsHttpService: AgentsHttpService = inject(AgentsHttpService),
   agentsChanelService: AgentsChanelService = inject(AgentsChanelService),
   commonsFacadeService: CommonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(AgentsAPiActions.deleteAgent),
      mergeMap(payload => {
        commonsFacadeService.setLoaderOn();
        return agentsHttpService.deleteOne(payload.id, payload.shouldMask).pipe(
          map((agent) => AgentsAPiActions.deleteAgentSuccess({agent: agent})),
          catchError((error: { message: string }) => of(AgentsAPiActions.deleteAgentFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);



export const subscribeAllAgentsChannel = createEffect(
  (actions$ = inject(Actions),
   agentsChanelService = inject(AgentsChanelService)) => {
    return actions$.pipe(
      ofType(AgentsChannelActions.subscribe),
      switchMap(payload => {
        if(!!payload.ids) {
          payload.ids.forEach(id => agentsChanelService.subscribeById(id));
        }
        return agentsChanelService.getChannelsValues().pipe(
          map((notification: AgentModel) => AgentsChannelActions.notificationReceived({notification: notification}))
        )
      })
    );
  },
  {functional: true}
);


export const unsubscribeAllAgentsChannels = createEffect(
  (actions$ = inject(Actions),
   agentsChannelService = inject(AgentsChanelService)) => {
    return actions$.pipe(
      ofType(AgentsChannelActions.unsubscribe, AgentsActions.removeAll, AgentsActions.clearStore),
      map(payload => {
          agentsChannelService.unSubscribeAll();
          return AgentsChannelActions.unsubscribeSuccess();
        }
      )
    );
  },
  {functional: true}
);

